import type { MouseEvent as ReactMouseEvent } from 'react';
import type {
  DataTableFilters,
  DataTableSavedViewsState,
  Filter,
  FilterValue,
} from '@customerio/pluma-components/react';
import type { EnvironmentsSourceWithID, Tag } from '@customerio/hydra-ui-api';
import type { DataIndexTotals } from '@/services/db/data-index/entity';
import { IN_USE_VIEW_ID } from './item';
import type { DataIndexTableRow } from './table-row';
import {
  chosenSourceGroups,
  groupSourceFilters,
  sourceGroupValue,
  sourceIdsOfGroups,
  type SourceFilterGroup,
  type SourceGroupTerms,
} from './source-filters';

/**
 * The list's narrowing controls: which rows are asked for, and the two views
 * that count them.
 *
 * All of it is server-side. The table holds one page of a set the backend
 * decides, so the filter bar edits the request rather than the rows, and the
 * counts beside the views come from their own totals read.
 */

/** What narrows the list, and how to narrow it differently. */
export type DataIndexNarrowingControls = {
  search: string;
  onSearchChange: (search: string) => void;
  tagIds: readonly number[];
  onTagIdsChange: (tagIds: number[]) => void;
  sourceIds: readonly number[];
  onSourceIdsChange: (sourceIds: number[]) => void;
  inUseOnly: boolean;
  onInUseOnlyChange: (inUseOnly: boolean) => void;
};

/**
 * Every tag and source the current family has, which is what the bar offers.
 * Only a tag's name reaches the bar: the rest of the wire model counts what
 * references the tag, which no filter shows.
 */
export type DataIndexFilterCatalogue = {
  tags: readonly Pick<Tag, 'id' | 'name'>[];
  sources: readonly EnvironmentsSourceWithID[];
};

/** Whatever the filter bar hands back, as the list of option values it is. */
function chosenValues(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

function tagFilter(tags: DataIndexFilterCatalogue['tags']): Filter {
  return {
    key: 'tags',
    label: 'Tags',
    type: 'checkbox',
    options: tags.map((tag) => ({ label: tag.name, value: String(tag.id) })),
  };
}

function sourceFilter(groups: readonly SourceFilterGroup[]): Filter {
  return {
    key: 'sources',
    label: 'Sources',
    type: 'checkbox',
    options: groups.map((group) => ({ label: group.label, value: sourceGroupValue(group) })),
  };
}

/**
 * The tags the filter offers: the catalogue's, plus any the URL carries that
 * it has not named. A value with no option behind it renders nothing, so an
 * id the catalogue cannot name yet stands for itself until it can.
 */
function withUnnamedTags(
  tags: DataIndexFilterCatalogue['tags'],
  chosen: readonly number[],
): DataIndexFilterCatalogue['tags'] {
  const named = new Set(tags.map((tag) => tag.id));
  const unnamed = [...new Set(chosen)].filter((id) => !named.has(id)).map((id) => ({ id, name: String(id) }));

  return unnamed.length === 0 ? tags : [...tags, ...unnamed];
}

/**
 * The same for sources, which are offered as groups: an id no group covers
 * becomes a group of its own, so the narrowing it stands for is visible and
 * can be cleared.
 */
function withUnnamedSources(
  groups: readonly SourceFilterGroup[],
  chosen: readonly number[],
): readonly SourceFilterGroup[] {
  const covered = new Set(groups.flatMap((group) => group.sourceIds));
  const unnamed = [...new Set(chosen)]
    .filter((id) => !covered.has(id))
    .map((id) => ({ label: String(id), sourceIds: [id] }));

  return unnamed.length === 0 ? groups : [...groups, ...unnamed];
}

function applyFilterChange(
  key: string,
  value: unknown,
  groups: readonly SourceFilterGroup[],
  narrowing: DataIndexNarrowingControls,
): void {
  const chosen = chosenValues(value);

  if (key === 'tags') {
    narrowing.onTagIdsChange(chosen.map(Number).filter(Number.isInteger));
  } else if (key === 'sources') {
    narrowing.onSourceIdsChange(sourceIdsOfGroups(groups, chosen));
  }
}

/**
 * The filter bar. A family with no tags offers no Tags filter rather than an
 * empty one, which is also what happens when the catalogue request fails: the
 * page still works, it just cannot be narrowed.
 *
 * A narrowing the URL carries is always shown, even before the catalogue can
 * name it. The table decides once, on its first render, whether it has a
 * filter row at all — so a value that arrives later than that appears
 * nowhere, and a shared link narrows the list with nothing to say why and no
 * way to clear it. Until the names land, an id stands for itself.
 */
export function dataIndexFilters(
  catalogue: DataIndexFilterCatalogue,
  narrowing: DataIndexNarrowingControls,
  terms: SourceGroupTerms,
): DataTableFilters {
  const groups = withUnnamedSources(groupSourceFilters(catalogue.sources, terms), narrowing.sourceIds);
  const tags = withUnnamedTags(catalogue.tags, narrowing.tagIds);

  const filters: Filter[] = [];
  if (tags.length > 0) {
    filters.push(tagFilter(tags));
  }
  if (groups.length > 0) {
    filters.push(sourceFilter(groups));
  }

  const values: FilterValue[] = [];
  if (narrowing.tagIds.length > 0) {
    values.push({ key: 'tags', value: narrowing.tagIds.map(String) });
  }
  const chosenSources = chosenSourceGroups(groups, narrowing.sourceIds);
  if (chosenSources.length > 0) {
    values.push({ key: 'sources', value: chosenSources });
  }

  return {
    filters,
    values,
    onChange: (key: string, value: unknown) => applyFilterChange(key, value, groups, narrowing),
    onClear: (key: string) => applyFilterChange(key, [], groups, narrowing),
    onClearAll: () => {
      narrowing.onTagIdsChange([]);
      narrowing.onSourceIdsChange([]);
    },
  };
}

/**
 * A view's name, with its size once that is known. The in-use count runs a
 * usage fill the backend budgets, so it can come back absent; a view whose
 * size nobody counted is named without one rather than claiming zero.
 */
function withCount(label: string, count: number | null | undefined): string {
  return count == null ? label : `${label} (${count})`;
}

/**
 * The two views: everything, and only what something references. Their counts
 * are a separate read from the rows, so both names appear bare for a moment
 * and gain their number when the totals arrive.
 */
export function dataIndexSavedViews(
  isEventBrowser: boolean,
  narrowing: DataIndexNarrowingControls,
  totals: DataIndexTotals | null,
): DataTableSavedViewsState<DataIndexTableRow, unknown, ReactMouseEvent> {
  const noun = isEventBrowser ? 'events' : 'attributes';

  return {
    views: [
      {
        id: IN_USE_VIEW_ID,
        name: withCount(`${isEventBrowser ? 'Events' : 'Attributes'} in use`, totals?.in_use),
      },
    ],
    defaultViewName: withCount(`All ${noun}`, totals?.all),
    activeViewId: narrowing.inUseOnly ? IN_USE_VIEW_ID : null,
    onActiveViewChange: (id) => narrowing.onInUseOnlyChange(id === IN_USE_VIEW_ID),
  };
}
